import React from "react";
import { SelectInputProps } from "../Form/Select/Input";
import Downshift, { GetMenuPropsOptions, GetItemPropsOptions } from "downshift";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";

interface Options {
    value: string | number;
    label: string;
}

export interface SelectProps {
    id?: string;
    autofocus?: boolean;
    disabled?: boolean;
    multiple?: boolean;
    required?: boolean;
    className?: string;
    placeholder?: string;
    onChange: (selectedItem: "" | string | number) => void;
    value: "" | string | number;
    name: string;
    options: Array<Options>;
    searchInputProps?: SelectInputProps;
    menuProps?: GetMenuPropsOptions;
    itemProps?: GetItemPropsOptions<Options>;
    itemToString?: (item: Options | null) => string;
    emptyItemValue?: Options;
}

export interface PerPageSelect {
    label?: React.ReactNode;
    inputProps: SelectProps;
}

const defaultItemToString = (item: Options | null) => (item ? item.label : "");

const PerPageSelector: React.FC<PerPageSelect> = ({ label, inputProps }) => {
    const selectedItem = inputProps.options.find((item) => item.value === inputProps.value);

    return (
        <div className="crud-per-page-selector">
            {label !== undefined && <label className="crud-per-page-selector__label">{label}</label>}
            <Downshift
                selectedItem={selectedItem || null}
                onChange={(selectedItem) => {
                    if (selectedItem) {
                        inputProps.onChange(selectedItem.value);
                    } else {
                        inputProps.onChange("");
                    }
                }}
                itemToString={inputProps.itemToString || defaultItemToString}
                initialInputValue=""
            >
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    isOpen,
                    highlightedIndex,
                    selectedItem,
                    openMenu,
                    toggleMenu,
                    itemToString,
                }) => {
                    return (
                        <div className="field-control">
                            <div className={inputProps.className || "field-control__select"}>
                                <input
                                    {...getInputProps({
                                        id: inputProps.id,
                                        name: inputProps.name,
                                        placeholder: selectedItem ? undefined : inputProps.placeholder,
                                        autoFocus: inputProps.autofocus,
                                        disabled: inputProps.disabled,
                                        onFocus: openMenu,
                                        onMouseDown: isOpen ? toggleMenu : openMenu, // mouseDown fired before focus
                                        type: "button",
                                        className: "field-control-select__input",
                                        value: inputProps.value,
                                        ...inputProps.searchInputProps,
                                    })}
                                />
                                <Icon icon={{ prefix: "fas", iconName: "caret-down" }} className={isOpen ? "up" : ""} />
                            </div>
                            {isOpen && (
                                <ul
                                    {...getMenuProps({
                                        className: "field-control-select__list",
                                        ...inputProps.menuProps,
                                    })}
                                >
                                    {inputProps.options.map((item, index) => (
                                        <li
                                            {...getItemProps({
                                                key: item.value,
                                                index: index + 1,
                                                item,
                                                className: `field-control-select__item ${
                                                    highlightedIndex === index + 1 ? "highlighted" : ""
                                                } ${selectedItem === item ? "selected" : ""}`,
                                                ...inputProps.itemProps,
                                            })}
                                        >
                                            {itemToString(item)}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                }}
            </Downshift>
        </div>
    );
};

export default PerPageSelector;
